import { useLocation } from "wouter";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Users, Shield, Trophy, Zap } from "lucide-react";
import gamePreviewImage from '@/assets/sparlegends.png';
import { useAuth } from "@/hooks/use-auth";
import { AuthLoading } from "@/components/auth/auth-loading";

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 12,
    },
  },
};

const FeatureCard = ({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) => (
  <motion.div
    variants={itemVariants}
    className="bg-black/60 backdrop-blur-sm rounded-xl border border-primary/30 p-6 hover:border-primary/70 transition-all"
  >
    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary/20 text-primary mb-4">
      {icon}
    </div>
    <h3 className="text-xl font-bold mb-2">{title}</h3>
    <p className="text-gray-400">{description}</p>
  </motion.div>
);

export default function LandingPage() {
  const [_, navigate] = useLocation();
  const { user, isLoading } = useAuth();

  // Show loading while checking auth
  if (isLoading) {
    return <AuthLoading />;
  }

  // Redirect to games if already authenticated
  if (user) {
    navigate("/games");
    return <AuthLoading />;
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-gray-900 to-black text-white">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-16 md:py-24">
        <div className="flex flex-col items-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-primary">
              Spar Legends
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-2xl">
              The ultimate card game experience with tournaments, rewards, and
              social gameplay
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="mb-16"
          >
            <Button
              size="lg"
              className="text-lg px-8 py-6 h-auto bg-primary hover:bg-primary/90"
              onClick={() => navigate("/auth")}
            >
              Login / Register
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>

          {/* Game Preview */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="relative w-full max-w-4xl rounded-lg overflow-hidden border border-primary/30 shadow-[0_0_40px_rgba(0,255,170,0.15)]"
          >
            <img
              src={gamePreviewImage}
              alt="Game Preview"
              className="w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end"></div>
          </motion.div>
        </div>
        <div className="p-6 md:p-8">
          <h3 className="text-2xl font-bold mb-2">Strategic Gameplay</h3>
          <p className="text-gray-300">
            Test your skills in our competitive card game environment
          </p>
        </div>
      </div>

      {/* Features Section */}
      <div className="container mx-auto px-4 pb-10">
        <motion.h2
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-3xl font-bold mb-12 text-center"
        >
          Game Features
        </motion.h2>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <FeatureCard
            icon={<Trophy className="h-6 w-6" />}
            title="Tournaments"
            description="Compete in daily and weekly tournaments with players from around the world"
          />
          <FeatureCard
            icon={<Users className="h-6 w-6" />}
            title="Multiplayer"
            description="Challenge friends or join matches with other players in real-time"
          />
          <FeatureCard
            icon={<Shield className="h-6 w-6" />}
            title="Unique Decks"
            description="Collect and customize decks with special cards and designs"
          />
          <FeatureCard
            icon={<Zap className="h-6 w-6" />}
            title="Achievements"
            description="Earn rewards and track your progress with our achievement system"
          />
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="bg-black/40 border-t border-primary/20 py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-gray-400 text-sm">
              © 2025 Spar Legends. All rights reserved.
            </div>
            <div className="flex gap-4">
              <a
                href="/terms-of-service"
                className="text-gray-400 hover:text-white text-sm"
              >
                Terms of Service
              </a>
              <a
                href="/privacy-policy"
                className="text-gray-400 hover:text-white text-sm"
              >
                Privacy Policy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
